import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, ShoppingBag, User, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const navigationItems = [
    { name: 'Squad & Staff', path: '/squad' },
    { name: 'Fixtures & Results', path: '/fixtures' },
    { name: 'News', path: '/news' },
    { name: 'About Us', path: '/about' },
    { name: 'Join Us', path: '/join-us' },
    { name: 'Sponsors', path: '/sponsors' },
    { name: 'Club Shop', path: '/shop' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const clubLogo = "https://customer-assets.emergentagent.com/job_cafc-platform/artifacts/d9rcitex_OurLogo.png";

  return (
    <nav className="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="relative">
              <img 
                src={clubLogo} 
                alt="Crawley Athletic FC" 
                className="h-16 w-16 filter drop-shadow-lg"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-2xl font-bold text-black" style={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: '2px' }}>
                Crawley Athletic
              </h1>
              <p className="text-sm text-gray-700" style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '1px' }}>
                FOOTBALL CLUB
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            <Search className="h-5 w-5 text-gray-700 cursor-pointer hover:text-black transition-colors" />
            <Link to="/shop" className="relative">
              <ShoppingBag className="h-5 w-5 text-gray-700 hover:text-black transition-colors" />
            </Link>
            
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-black transition-colors"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden sm:inline text-sm font-medium" style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.5px' }}>
                    {user.full_name}
                  </span>
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white bg-opacity-95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-black">{user.full_name}</p>
                        <p className="text-xs text-gray-600 capitalize">{user.role}</p>
                      </div>
                      
                      {user.role === 'player' && (
                        <Link
                          to="/players-area"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-black"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Players' Area
                        </Link>
                      )}
                      

                      
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/login" 
                className="btn text-sm py-2 px-4 bg-black bg-opacity-80 hover:bg-opacity-100 text-white border border-black backdrop-blur-sm"
                style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase' }}
              >
                Players' Area
              </Link>
            )}

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 border-opacity-30 bg-white bg-opacity-90 backdrop-blur-md">
            <div className="py-4 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`block px-4 py-2 text-base font-medium rounded-lg transition-colors ${
                    location.pathname === item.path
                      ? 'bg-black bg-opacity-10 text-black'
                      : 'text-gray-700 hover:bg-black hover:bg-opacity-5 hover:text-black'
                  }`}
                  style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.5px', textTransform: 'uppercase' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Header;